import { ReactCountryFlag } from "react-country-flag";
import { ColModel } from "../components/GenericTableRow";
import { formatCoordinates } from "./helpers";
import {
	Portfolio,
	Site,
	isPortfolio,
} from "../../features/sites/sites.module";
import { HamburgerMenu } from "../components/HamburgerMenu";
import { HamburgerOptions } from "../components/HamburgerOptions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import AddIcon from "@mui/icons-material/Add";
import { useMemo } from "react";
import { SiteTechnicalDetails } from "../components/SiteTechnicalDetails";
import _ from "lodash";
import PicColumn from "../components/PicColumn";


export interface TenderFormSiteTableModelParams {
	showRefreshCluster?: boolean;
	handleChange?: any;
	values?: any;
	errors?: any;
	touched?: any;
	setFieldValue?: any;
}

export const useTenderFormSitePricingModel = (
	params: TenderFormSiteTableModelParams
) => {
	const {
		showRefreshCluster = true,
		values,
		setFieldValue,
		errors,
		touched,
	} = params;

	const rowId = {
		id: "1",
		header: () => "Id",
		xs: 1,
		hidden: true,
		accessor: (m: Site) => m.id,
	};
	const rowName = {
		id: "2",
		header: () => "Name",
		xs: 2,
		accessor: (m: Site) => {
			return (
				<>
					{m.name}{" "}
					{"children" in m && m.children
						? `(${m.children.length})`
						: ""}
				</>
			);
		},
	};
	const rowCountry = {
		id: "4",
		header: () => "Country",
		xs: 1,
		accessor: (m: Site) => (
			<>
				{"country" in m && (
					<>
						<ReactCountryFlag
							countryCode={m.country ?? ""}
							svg
							title={m.country ?? ""}
						/>

						{m.country}
					</>
				)}
			</>
		),
	};
	const rowTechnology = {
		id: "8",
		header: () => "Technology",
		xs: 1,
		accessor: (m: Site) => "techno" in m && m?.techno?.name,
	};
	const rowPIC = {
		id: "9",
		header: () => "PIC",
		xs: 1,
		accessor: (m: Site | Portfolio) => <PicColumn item={m}/>
	};
	const rowInstalledCapacity = {
		id: "10",
		header: () => "Installed capacity",
		xs: 1,
		accessor: (m: Site) => (
			<>
				{"installed_capacity_mw" in m &&
					(m.installed_capacity_mw ?? "")}
			</>
		),
	};
	const rowParkAge = {
		id: "11",
		header: () => "Park age",
		xs: 1,
		accessor: (m: Site) => {
			return m?.park_age;
		},
	};
	const rowTechnicalDetails = {
		id: "12",
		header: () => "Technical details",
		xs: 2,
		accessor: (m: Site | Portfolio) => <SiteTechnicalDetails site={m} />,
	};
	const rowLatitude = {
		id: "13",
		header: () => "Latitude",
		xs: 1,
		accessor: (m: Site | Portfolio) =>
			isPortfolio(m) ? "" : formatCoordinates(4, m).latitude,
	};
	const rowLongitude = {
		id: "14",
		header: () => "Longitude",
		xs: 1,
		accessor: (m: Site | Portfolio) =>
			isPortfolio(m) ? "" : formatCoordinates(4, m).longitude,
	};
	const rowOptions = {
		id: "15",
		header: () => " ",
		hidden: !showRefreshCluster,
		xs: 1,
		accessor: (m: Site) =>
			isPortfolio(m) ? null : (
				<HamburgerMenu key={`hamburger${m.asset_id}`}>
					<HamburgerOptions
						title={"Refresh cluster"}
						action={() => {}}
					/>
				</HamburgerMenu>
			),
	};
	const rowRealizedTimeseries = {
		id: "16",
		noTooltip: true,
		header: () => "Realized Timeseries",
		xs: 3,
		accessor: (m: Site) => {
			const timeserieNames = m.ts?.map((ts) => ts.timeserie_name) || [];
			return (
				<CustomAutocomplete
					key={`autocomplete_${m.asset_id}`}
					sx={{
						"& .MuiInputLabel-shrink": {
							display: "none",
						},
						width: "100%",
						marginBottom: 0,
					}}
					required={false}
					fetchData={() => {}}
					data={timeserieNames}
					label={"Tourbillon Ref"}
					name={m.asset_id}
					setFieldValue={(field, value) => {
						const existingTs = _.find(
							m.ts,
							(ts) => ts.timeserie_name === value
						);
						if (existingTs) {
							return setFieldValue(field, existingTs);
						} else if (typeof value === "string") {
							return setFieldValue(field, {
								ts_type: "master_site",
								timeserie_name: value,
								cleaned: false,
							});
						}
					}}
					renderOption={(optionProps, option: any, state) => {
						if (
							option === state.inputValue &&
							!_.includes(timeserieNames, option)
						) {
							return (
								<li {...optionProps} key={option}>
									<AddIcon data-testid="test-add-icon" />
									<span
										style={{
											marginLeft: 8,
										}}
									>
										{option}
									</span>
								</li>
							);
						}
						return (
							<li {...optionProps} key={option}>
								<span
									style={{
										marginLeft: 8,
									}}
								>
									{option}
								</span>
							</li>
						);
					}}
					value={values[m.asset_id || ""]?.timeserie_name || ""}
					freeSolo={true}
					paperSx={{
						paper: {
							sx: {
								width: "fit-content",
								maxWidth: "500px",
							},
						},
					}}
					error={!!errors[m.asset_id || ""]}
					helperText={
						!!errors[m.asset_id || ""] &&
						Object.entries(touched).length > 0 &&
						!touched[m.asset_id || ""]
							? errors[m.asset_id || ""]
							: ""
					}
				/>
			);
		},
	};
	const rowModel: ColModel<Site>[] = useMemo(() => {
		const test = [
			rowId,
			rowName,
			rowCountry,
			rowTechnology,
			rowPIC,
			rowInstalledCapacity,
			rowParkAge,
			rowTechnicalDetails,
			rowLatitude,
			rowLongitude,
			rowOptions,
			rowRealizedTimeseries,
		];
		return test;
	}, [values, errors, touched]);

	return rowModel;
};
